import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, MapPin, Mic, ChevronDown, Check, X,
  Clock, Building2, Briefcase, Hammer, TrendingUp,
  Home, User, Building, Map as MapIcon, Key, IndianRupee, LayoutGrid, Sofa
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { api } from "@/lib/api";
import { toast } from "sonner";

const BRAND = "#0078DB";

const TABS = ["Buy", "Rent", "Commercial", "PG/Hostel", "Plot/Land"];

const TAB_ICONS = {
  Buy: Home,
  Rent: Key,
  Commercial: Building,
  "PG/Hostel": User,
  "Plot/Land": MapIcon,
};

const FILTER_ICONS = {
  budget: IndianRupee,
  rent: IndianRupee,
  area: MapIcon,
  plot_area: MapIcon,
  builder: Hammer,
  bhk: LayoutGrid,
  construction: Building,
  posted_by: User,
  furnishing: Sofa,
  availability: Clock,
  ownership: Building2,
  land_type: MapIcon,
  possession: Clock,
  launch_status: TrendingUp,
  commercial_type: Building2,
  project_type: Building2,
};

const TAB_TYPES = {
  Buy: {
    Residential: [
      "Flat / Apartment", "Independent / Builder Floor",
      "Independent House / Villa", "Residential Land",
      "1 RK / Studio Apartment", "Farm House", "Serviced Apartment",
    ],
    Commercial: [],
  },
  Rent: {
    Residential: [
      "Flat / Apartment", "Independent House / Villa",
      "1 RK / Studio Apartment", "PG / Co-living", "Serviced Apartment",
    ],
    Commercial: [],
  },
  "New Launch": {
    Residential: ["Flat / Apartment", "Independent / Builder Floor", "Villa", "Plot"],
    Commercial: ["Office Space", "Shop / Showroom"],
  },
  Commercial: {
    Residential: [],
    Commercial: [
      "Office Space", "Shop / Showroom", "Warehouse / Godown",
      "Industrial Building", "Commercial Land", "Co-working",
    ],
  },
  "Plot/Land": {
    Residential: ["Residential Land"],
    Commercial: ["Commercial Land", "Industrial Land", "Agricultural Land"],
  },
  "PG/Hostel": {
    Residential: ["PG / Co-living", "Hostel"],
    Commercial: [],
  },
};

/* -------- Tab-aware filter configuration -------- */
const BHK_OPTIONS = ["1 BHK", "2 BHK", "3 BHK", "4 BHK", "5+ BHK"];
const CONSTRUCTION_OPTIONS = ["New Launch", "Under Construction", "Ready To Move"];
const POSTED_BY_OPTIONS = ["Owner", "Builder", "Dealer"];
const FURNISHING_OPTIONS = ["Furnished", "Semi-Furnished", "Unfurnished"];
const AVAILABILITY_OPTIONS = ["Immediately", "Within 15 Days", "Within 30 Days", "Within 60 Days"];
const OWNERSHIP_OPTIONS = ["Freehold", "Leasehold", "Co-operative Society", "Power Of Attorney"];
const LAND_TYPE_OPTIONS = ["Residential", "Commercial", "Agricultural", "Industrial"];
const POSSESSION_OPTIONS = ["Ready To Move", "Within 6 Months", "Within 1 Year", "Beyond 1 Year"];
const LAUNCH_STATUS_OPTIONS = ["Pre-Launch", "Just Launched", "Open For Booking"];

// Search URLs use backend-owned canonical values. Labels stay user-friendly,
// while these tokens remain stable across home search, navbar links and APIs.
const PROPERTY_TYPE_TOKENS = {
  "Flat / Apartment": ["FLAT"],
  "Independent / Builder Floor": ["HOUSE"],
  "Independent House / Villa": ["HOUSE", "VILLA"],
  "Villa": ["VILLA"],
  "Residential Land": ["LAND", "PLOT"],
  "Plot": ["LAND", "PLOT"],
  "1 RK / Studio Apartment": ["FLAT"],
  "Farm House": ["HOUSE"],
  "Serviced Apartment": ["FLAT"],
  "PG / Co-living": ["PG_HOSTEL"],
  "Hostel": ["PG_HOSTEL"],
  "Office Space": ["COMMERCIAL"],
  "Shop / Showroom": ["COMMERCIAL"],
  "Warehouse / Godown": ["COMMERCIAL"],
  "Industrial Building": ["COMMERCIAL"],
  "Commercial Land": ["LAND", "PLOT"],
  "Industrial Land": ["LAND", "PLOT"],
  "Agricultural Land": ["LAND"],
  "Co-working": ["COMMERCIAL"],
};
const canonical = (value) => String(value || "").trim().toUpperCase().replace(/[\s-]+/g, "_");

// Per-tab dynamic filter chip list
const TAB_FILTERS = {
  "Buy":        ["budget", "bhk", "construction", "posted_by", "furnishing"],
  "Rent":       ["rent", "furnishing", "availability", "posted_by"],
  "Commercial": ["commercial_type", "area", "budget", "ownership", "posted_by"],
  "Plot/Land":  ["plot_area", "land_type", "budget", "posted_by"],
  "PG/Hostel": ["rent", "posted_by"],
};

const fmtCr = (n) => {
  if (n >= 1) return `₹${n.toFixed(n % 1 ? 2 : 0)} Cr`;
  return `₹${Math.round(n * 100)} L`;
};

const RECENT_KEY = "shreehomes_recent_searches";
const readRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; }
};
const pushRecent = (entry) => {
  if (!entry || !entry.label) return;
  const list = readRecent().filter(r => r.label !== entry.label);
  list.unshift({ ...entry, at: Date.now() });
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 8)));
};
const removeRecent = (label) => {
  const list = readRecent().filter(r => r.label !== label);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  return list;
};
const clearAllRecent = () => {
  localStorage.removeItem(RECENT_KEY);
  return [];
};

// Escape regex characters in user input
const escRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const highlight = (text, q) => {
  if (!q || !text) return text;
  const re = new RegExp(`(${escRe(q)})`, "ig");
  return String(text).split(re).map((part, i) =>
    re.test(part) ? <span key={i} className="suggest-mark">{part}</span> : <span key={i}>{part}</span>
  );
};

export default function MegaSearchBar() {
  const [activeTab, setActiveTab] = useState("Buy");
  const [propType, setPropType] = useState([]);
  const [city, setCity] = useState("");
  // Budget in Cr for Buy/Commercial/etc. ; in K/month for Rent.
  const [budget, setBudget] = useState([0.5, 5]);
  const [budgetTouched, setBudgetTouched] = useState(false);
  // Tab-aware multi-select filter state
  const [filters, setFilters] = useState({}); // { bhk:[], construction:[], posted_by:[], furnishing:[], availability:[], ownership:[], land_type:[], possession:[], builder:"", launch_status:[], commercial_type:[] }
  const [areaRange, setAreaRange] = useState([0, 5000]);
  const [areaTouched, setAreaTouched] = useState(false);

  // Autocomplete state
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggest, setSuggest] = useState(null); // {cities, localities, projects, builders, properties}
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [locating, setLocating] = useState(false);
  const [listening, setListening] = useState(false);
  const [recent, setRecent] = useState(readRecent());
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  const navigate = useNavigate();

  const toggleArr = (setter, list, v) =>
    setter(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const isRent = activeTab === "Rent";
  // Budget unit: Cr for Buy etc., ₹K/month for Rent
  const budgetMax = isRent ? 200 : 10; // 200K/mo or 10 Cr
  const budgetStep = isRent ? 5 : 0.25;

  const fmtBudget = useCallback((n) => {
    if (isRent) {
      if (n >= 100) return `₹${(n / 100).toFixed(1)}L/mo`;
      return `₹${Math.round(n)}K/mo`;
    }
    if (n >= 1) return `₹${n.toFixed(n % 1 ? 2 : 0)} Cr`;
    return `₹${Math.round(n * 100)} L`;
  }, [isRent]);

  const switchTab = (t) => {
    setActiveTab(t);
    setPropType([]);
    setSuggest(null);
    // Reset filters appropriately
    setBudget(t === "Rent" ? [10, 100] : [0.5, 5]);
    setBudgetTouched(false);
    setFilters({});
    setAreaRange([0, 5000]);
    setAreaTouched(false);
  };

  const toggleFilter = (key, val) => {
    setFilters((prev) => {
      const cur = prev[key] || [];
      const arr = Array.isArray(cur) ? cur : [cur].filter(Boolean);
      return { ...prev, [key]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] };
    });
  };
  const setFilter = (key, val) => setFilters((prev) => ({ ...prev, [key]: val }));
  const clearFilter = (key) => setFilters((prev) => ({ ...prev, [key]: undefined }));

  const defaultLabel = activeTab === "Commercial"
    ? "All Commercial"
    : activeTab === "Plot/Land"
      ? "All Plots/Land"
      : activeTab === "PG/Hostel"
        ? "All PG/Hostels"
        : "All Residential";

  const propLabel = propType.length === 0
    ? defaultLabel
    : propType.length === 1
      ? propType[0]
      : `${propType.length} selected`;

  const flatSuggestions = useMemo(() => {
    if (!suggest) return [];
    const items = [];
    suggest.cities?.forEach(c => items.push({ kind: "city", label: c.name, sub: `${c.count} properties` }));
    suggest.localities?.forEach(l => items.push({ kind: "locality", label: l.name, sub: `${l.city} · ${l.count} properties` }));
    suggest.projects?.forEach(p => items.push({ kind: "project", label: p.name, sub: `${p.city} · ${p.count} units` }));
    suggest.builders?.forEach(b => items.push({ kind: "builder", label: b.name, sub: `${b.count} listings` }));
    suggest.properties?.slice(0, 5).forEach(p => items.push({
      kind: "property", label: p.title, sub: `${p.locality}, ${p.city}`, property_id: p.property_id,
    }));
    return items;
  }, [suggest]);

  // Debounced fetch
  useEffect(() => {
    const handler = setTimeout(async () => {
      const q = city.trim();
      if (q.length < 1) {
        setSuggest(null);
        return;
      }
      setSuggestLoading(true);
      try {
        const { data } = await api.get(`/search/suggest?q=${encodeURIComponent(q)}&tab=${encodeURIComponent(activeTab)}&limit=6`);
        if (data && Array.isArray(data.suggestions)) {
          const citiesMap = new Map();
          const localitiesMap = new Map();
          data.suggestions.forEach(s => {
            if (s.city) {
              citiesMap.set(s.city, (citiesMap.get(s.city) || 0) + 1);
            }
            if (s.locality && s.city) {
              const key = `${s.locality}::${s.city}`;
              localitiesMap.set(key, (localitiesMap.get(key) || 0) + 1);
            }
          });
          const cities = Array.from(citiesMap.entries()).map(([name, count]) => ({ name, count }));
          const localities = Array.from(localitiesMap.entries()).map(([key, count]) => {
            const [name, city] = key.split("::");
            return { name, city, count };
          });
          setSuggest({
            cities,
            localities,
            projects: [],
            builders: [],
            properties: []
          });
        } else {
          setSuggest(data);
        }
      } catch (e) {
        setSuggest(null);
      } finally {
        setSuggestLoading(false);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [city, activeTab]);

  // Close suggestion on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!inputRef.current) return;
      if (
        !inputRef.current.contains(e.target) &&
        !(popoverRef.current && popoverRef.current.contains(e.target))
      ) {
        setSuggestOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const onSubmit = useCallback((overrides = {}) => {
    const _city = overrides.city ?? city.trim();
    const params = new URLSearchParams();
    const selectedIntent = overrides.intent ?? activeTab;
    if (selectedIntent === "Rent") params.set("listing_type", "RENT");
    if (selectedIntent === "Buy" || selectedIntent === "Commercial" || selectedIntent === "Plot/Land") params.set("listing_type", "SELL");
    if (selectedIntent === "Commercial") params.set("category", "COMMERCIAL");
    if (selectedIntent === "PG/Hostel") params.set("category", "PG_HOSTEL");
    if (selectedIntent === "Plot/Land") params.set("property_types", "LAND,PLOT");
    // Issue #6: only a city explicitly chosen from suggestions filters by city
    // (exact). Free-typed text is sent as `q` so the backend's fuzzy/alias/
    // relevance keyword search (title, locality, landmark, project, etc.) applies.
    if (overrides.city) {
      params.set("city", overrides.city);
    } else if (_city) {
      params.set("q", _city);
    }
    if (overrides.locality) params.set("locality", overrides.locality);
    if (overrides.project_name) params.set("q", overrides.project_name);
    if (overrides.builder_name) params.set("q", overrides.builder_name);
    const propertyTypes = [...new Set(propType.flatMap((label) => PROPERTY_TYPE_TOKENS[label] || [canonical(label)]))];
    if (propertyTypes.length) params.set("property_types", propertyTypes.join(","));
    // Only apply budget if user moved sliders
    if (budgetTouched) {
      const mul = isRent ? 1000 : 1e7;
      if (budget[0] > 0) params.set("min_price", String(budget[0] * mul));
      if (budget[1] < budgetMax) params.set("max_price", String(budget[1] * mul));
    }
    if (areaTouched) {
      if (areaRange[0] > 0) params.set("min_area", String(areaRange[0]));
      if (areaRange[1] < 5000) params.set("max_area", String(areaRange[1]));
    }
    // Tab-aware filters → backend params
    const f = filters;
    if (f.bhk?.length) params.set("bedrooms", String(parseInt(f.bhk[0], 10) || 1));
    if (f.construction?.length) params.set("construction_status", f.construction.map(canonical).join(","));
    if (f.posted_by?.length) params.set("posted_by", f.posted_by.map(canonical).join(","));
    if (f.furnishing?.length) params.set("furnished", f.furnishing.map(canonical).join(","));
    if (f.availability?.length) params.set("availability", canonical(f.availability[0]));
    if (f.ownership?.length) params.set("ownership_type", f.ownership.map(canonical).join(","));
    if (f.land_type?.length) params.set("property_types", "LAND,PLOT");
    if (f.possession?.length) params.set("construction_status", f.possession.map(canonical).join(","));
    if (f.commercial_type?.length || f.project_type?.length) params.set("property_types", "COMMERCIAL");
    if (f.builder) params.set("q", f.builder);
    if (f.launch_status?.length) params.set("construction_status", f.launch_status.map(canonical).join(","));
    if (overrides.query) params.set("q", overrides.query);
    if (overrides.near_lat != null && overrides.near_lng != null) {
      params.set("near_lat", String(overrides.near_lat));
      params.set("near_lng", String(overrides.near_lng));
      params.set("radius_km", "25");
    }
    if (!overrides.skipRecent) {
      // Store the canonical query string so a locality/project/keyword recent
      // search replays the exact filters instead of becoming an exact-city filter.
      pushRecent({
        label: overrides.label || _city || activeTab,
        intent: overrides.intent ?? activeTab,
        city: overrides.city || "",
        query: overrides.query || (!overrides.city ? _city : ""),
        kind: overrides.kind || "search",
        search: params.toString(),
      });
      setRecent(readRecent());
    }
    setSuggestOpen(false);
    navigate(`/properties?${params.toString()}`);
  }, [city, activeTab, propType, budget, budgetTouched, isRent, budgetMax, areaRange, areaTouched, filters, navigate]);

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location is not supported by this browser.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setLocating(false);
        setCity("Near my location");
        // Clear any stale typed city/query. Coordinates are intentionally not
        // persisted in recent searches because they are sensitive location data.
        onSubmit({ city: "", near_lat: coords.latitude, near_lng: coords.longitude, label: "Near my location", kind: "nearby", skipRecent: true });
      },
      () => {
        setLocating(false);
        toast.error("Location permission was not granted. Search by city or locality instead.");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };

  const openRecent = (entry) => {
    setCity(entry.label || "");
    if (entry.search) {
      pushRecent(entry);
      setRecent(readRecent());
      setSuggestOpen(false);
      navigate(`/properties?${entry.search}`);
      return;
    }
    // Backward-compatible handling for recent searches saved by older builds.
    if (entry.kind === "city" && entry.city) {
      onSubmit({ city: entry.city, label: entry.label, kind: entry.kind });
    } else {
      onSubmit({ query: entry.query || entry.label, label: entry.label, kind: entry.kind || "recent" });
    }
  };

  const startVoiceSearch = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice search is not supported by this browser.");
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => setListening(false);
    recognition.onerror = () => toast.error("Voice search could not hear you. Please try again.");
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript?.trim();
      if (!transcript) return;
      setCity(transcript);
      onSubmit({ query: transcript, label: transcript, kind: "voice" });
    };
    recognition.start();
  };

  const pickSuggestion = (s) => {
    if (s.kind === "property" && s.property_id) {
      pushRecent({ label: s.label, kind: "property" });
      setRecent(readRecent());
      setSuggestOpen(false);
      navigate(`/properties/${s.property_id}`);
      return;
    }
    if (s.kind === "city") {
      setCity(s.label);
      onSubmit({ city: s.label, label: s.label, kind: "city" });
    } else if (s.kind === "locality") {
      setCity(s.sub.split("·")[0].trim());
      onSubmit({ city: s.sub.split("·")[0].trim(), locality: s.label, label: s.label, kind: "locality" });
    } else if (s.kind === "project") {
      onSubmit({ project_name: s.label, label: s.label, kind: "project" });
    } else if (s.kind === "builder") {
      onSubmit({ builder_name: s.label, label: s.label, kind: "builder" });
    }
  };

  const onKeyDown = (e) => {
    if (!suggestOpen && (e.key === "ArrowDown" || e.key === "Enter")) {
      setSuggestOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, flatSuggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0 && flatSuggestions[activeIndex]) {
        pickSuggestion(flatSuggestions[activeIndex]);
      } else {
        onSubmit();
      }
    } else if (e.key === "Escape") {
      setSuggestOpen(false);
    }
  };

  return (
    <div
      className="bg-white rounded-[28px] border border-slate-200/50 shadow-[0_24px_70px_rgba(0,0,0,0.06)] overflow-visible w-full transform-gpu"
      style={{ transform: "translateZ(0)", willChange: "transform" }}
      data-testid="mega-search-bar"
    >
      {/* Top Tab Row */}
      <div className="flex items-center gap-2 px-6 pt-4 pb-0 border-b border-slate-100 overflow-x-auto scrollbar-none">
        {TABS.map((t) => {
          const active = activeTab === t;
          const IconComponent = TAB_ICONS[t] || Home;
          return (
            <button
              key={t}
              type="button"
              onClick={() => switchTab(t)}
              className={`relative flex items-center gap-2 px-4 pb-3 pt-2 text-[14px] font-semibold whitespace-nowrap transition-all duration-200 ${
                active 
                  ? "text-blue-600 border-b-2 border-blue-600 font-bold" 
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-50/50 rounded-t-xl"
              }`}
              data-testid={`tab-${t.replace(/[\s/]/g,"-").toLowerCase()}`}
              aria-pressed={active}
            >
              <IconComponent className={`w-4 h-4 ${active ? "text-blue-600" : "text-slate-450"}`} />
              <span>{t}</span>
            </button>
          );
        })}
      </div>

      {/* Main Search Row */}
      <div className="flex flex-col lg:flex-row items-stretch gap-4 p-5 lg:p-6 bg-white rounded-b-[28px]">
        {/* Property Type Multiselect */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex items-center justify-between gap-2.5 px-4 h-[56px] rounded-2xl bg-[#f8fafc] border border-slate-100 hover:bg-[#f1f5f9] hover:border-slate-200 transition-all duration-200 text-left text-[14px] text-[#303030] shrink-0 lg:w-[240px]"
              data-testid="proptype-trigger"
              aria-haspopup="true"
            >
              <div className="flex items-center gap-2 truncate">
                <LayoutGrid className="w-5 h-5 text-blue-600 shrink-0" />
                <span className="font-semibold text-slate-800 truncate">{propLabel}</span>
              </div>
              <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[300px] p-0 bg-white border border-slate-200/80 rounded-xl shadow-[0_12px_40px_rgba(0,120,219,0.05)] overflow-hidden" align="start" side="bottom" avoidCollisions sideOffset={6}>
            <div className="p-3 max-h-[380px] overflow-y-auto">
              {TAB_TYPES[activeTab]?.Residential?.length > 0 && (
                <>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#737373] mb-2">Residential</p>
                  <div className="space-y-1 mb-3">
                    {TAB_TYPES[activeTab].Residential.map(t => (
                      <CheckRow
                        key={t}
                        label={t}
                        checked={propType.includes(t)}
                        onClick={() => toggleArr(setPropType, propType, t)}
                        testid={`prop-r-${t.replace(/[\s/]/g,"-").toLowerCase()}`}
                      />
                    ))}
                  </div>
                </>
              )}
              {TAB_TYPES[activeTab]?.Commercial?.length > 0 && (
                <>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#737373] mb-2">Commercial</p>
                  <div className="space-y-1">
                    {TAB_TYPES[activeTab].Commercial.map(t => (
                      <CheckRow
                        key={t}
                        label={t}
                        checked={propType.includes(t)}
                        onClick={() => toggleArr(setPropType, propType, t)}
                        testid={`prop-c-${t.replace(/[\s/]/g,"-").toLowerCase()}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-[#E5E7EB] px-3 py-2">
              <button
                type="button"
                onClick={() => setPropType([])}
                className="text-[12px] font-semibold text-[#737373] hover:text-[#303030]"
                data-testid="proptype-clear"
              >Clear</button>
              <span className="text-[11px] text-[#737373]">{propType.length} selected</span>
            </div>
          </PopoverContent>
        </Popover>

        {/* Search Input + Suggestions */}
        <div ref={inputRef} className="relative flex-1">
          <div className="flex items-center bg-[#f8fafc] border border-slate-100 rounded-2xl hover:bg-[#f1f5f9] focus-within:bg-white focus-within:border-blue-500/30 focus-within:ring-4 focus-within:ring-blue-500/5 transition-all duration-200 h-[56px] px-4">
            <Search className="w-5 h-5 text-blue-600 shrink-0" />
            <input
              type="text"
              value={city}
              onChange={(e) => { setCity(e.target.value); setSuggestOpen(true); setActiveIndex(-1); }}
              onFocus={() => setSuggestOpen(true)}
              onKeyDown={onKeyDown}
              placeholder="Search by city, locality, project, or builder"
              className="flex-1 mx-3 bg-transparent text-[14px] text-[#1e293b] font-medium placeholder:text-slate-400 focus:outline-none"
              data-testid="search-input"
              aria-autocomplete="list"
              aria-expanded={suggestOpen}
              aria-controls="search-suggest-listbox"
            />
            <div className="hidden sm:flex items-center gap-2 mr-1">
              <button
                type="button"
                className="w-9 h-9 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 flex items-center justify-center hover:bg-slate-50 transition active:scale-95 duration-150"
                aria-label="Use my location"
                data-testid="search-location-icon"
                onClick={useMyLocation}
                disabled={locating}
                title={locating ? "Finding your location" : "Search near my location"}
              >
                <MapPin className="w-4 h-4 text-blue-600" />
              </button>
              <button
                type="button"
                className="w-9 h-9 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] border border-slate-100 flex items-center justify-center hover:bg-slate-50 transition active:scale-95 duration-150"
                aria-label={listening ? "Listening for voice search" : "Voice search"}
                data-testid="search-voice-icon"
                onClick={startVoiceSearch}
                disabled={listening}
                title={listening ? "Listening…" : "Search using your voice"}
              >
                <Mic className="w-4 h-4 text-blue-600" />
              </button>
            </div>
          </div>

          {suggestOpen && (city.trim().length > 0 || recent.length > 0) && (
            <div
              ref={popoverRef}
              id="search-suggest-listbox"
              role="listbox"
              style={{ transform: "translate3d(0,0,0)", willChange: "transform" }}
              className="absolute left-0 right-0 top-[calc(100%+6px)] bg-white border border-slate-200/80 rounded-xl shadow-[0_16px_48px_rgba(0,120,219,0.06)] z-[60] overflow-hidden max-h-[420px] overflow-y-auto transition-all duration-150 animate-in fade-in-50 slide-in-from-top-1"
              data-testid="suggest-panel"
            >
                {city.trim().length === 0 && recent.length > 0 && (
                  <div className="p-2">
                    <div className="flex items-center justify-between px-3 py-1.5">
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#737373]">Recent Searches</p>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setRecent(clearAllRecent()); }}
                        className="text-[10px] font-bold text-[#737373] hover:text-[#EF4444] transition"
                        data-testid="recent-clear-all"
                      >Clear all</button>
                    </div>
                    {recent.slice(0, 6).map((r, i) => (
                      <div
                        key={`${r.label}-${i}`}
                        className="group flex items-center gap-2 px-3 py-2 rounded-md hover:bg-[#F5F8FB] transition"
                        data-testid={`recent-${i}`}
                      >
                        <button
                          type="button"
                          className="flex items-center gap-3 flex-1 min-w-0 text-left"
                          onClick={() => openRecent(r)}
                        >
                          <Clock className="w-4 h-4 text-[#737373] shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] text-[#303030] font-medium truncate">{r.label}</p>
                            {r.kind && r.kind !== "search" && (
                              <p className="text-[11px] text-[#737373] capitalize">{r.kind}</p>
                            )}
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setRecent(removeRecent(r.label)); }}
                          className="w-7 h-7 rounded-full grid place-items-center text-[#9CA3AF] hover:bg-red-50 hover:text-red-500 transition opacity-60 group-hover:opacity-100"
                          aria-label={`Remove ${r.label}`}
                          data-testid={`recent-remove-${i}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {city.trim().length > 0 && (
                  <>
                    {suggestLoading && (
                      <div className="px-4 py-3 text-[12px] text-[#737373]" data-testid="suggest-loading">Searching…</div>
                    )}

                    {!suggestLoading && flatSuggestions.length === 0 && (
                      <div className="px-4 py-6 text-center text-[13px] text-[#737373]" data-testid="suggest-empty">
                        No matches for &quot;<span className="font-semibold text-[#303030]">{city}</span>&quot;.
                        <br />
                        <button
                          type="button"
                          className="mt-2 text-[#0078DB] font-semibold text-[12px]"
                          onClick={() => onSubmit()}
                        >Search anyway</button>
                      </div>
                    )}

                    {!suggestLoading && (
                      <SuggestionGroup
                        title="Cities"
                        icon={MapPin}
                        items={suggest?.cities || []}
                        getLabel={(c) => c.name}
                        getSub={(c) => `${c.count} properties`}
                        q={city}
                        onPick={(c) => pickSuggestion({ kind: "city", label: c.name })}
                        testPrefix="sug-city"
                        flat={flatSuggestions}
                        activeIndex={activeIndex}
                        baseIndex={0}
                      />
                    )}
                    {!suggestLoading && (
                      <SuggestionGroup
                        title="Localities"
                        icon={MapPin}
                        items={suggest?.localities || []}
                        getLabel={(l) => l.name}
                        getSub={(l) => `${l.city} · ${l.count} properties`}
                        q={city}
                        onPick={(l) => pickSuggestion({ kind: "locality", label: l.name, sub: `${l.city} · ${l.count}` })}
                        testPrefix="sug-loc"
                        flat={flatSuggestions}
                        activeIndex={activeIndex}
                        baseIndex={(suggest?.cities || []).length}
                      />
                    )}
                    {!suggestLoading && (
                      <SuggestionGroup
                        title="Projects"
                        icon={Building2}
                        items={suggest?.projects || []}
                        getLabel={(p) => p.name}
                        getSub={(p) => `${p.city} · ${p.count} units`}
                        q={city}
                        onPick={(p) => pickSuggestion({ kind: "project", label: p.name })}
                        testPrefix="sug-proj"
                        flat={flatSuggestions}
                        activeIndex={activeIndex}
                        baseIndex={(suggest?.cities || []).length + (suggest?.localities || []).length}
                      />
                    )}
                    {!suggestLoading && (
                      <SuggestionGroup
                        title="Builders"
                        icon={Hammer}
                        items={suggest?.builders || []}
                        getLabel={(b) => b.name}
                        getSub={(b) => `${b.count} listings`}
                        q={city}
                        onPick={(b) => pickSuggestion({ kind: "builder", label: b.name })}
                        testPrefix="sug-build"
                        flat={flatSuggestions}
                        activeIndex={activeIndex}
                        baseIndex={(suggest?.cities || []).length + (suggest?.localities || []).length + (suggest?.projects || []).length}
                      />
                    )}
                    {!suggestLoading && (suggest?.properties || []).length > 0 && (
                      <div className="border-t border-[#F1F3F5] py-1">
                        <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#737373]">Properties</p>
                        {suggest.properties.slice(0, 5).map((p, i) => {
                          const baseIdx =
                            (suggest?.cities || []).length +
                            (suggest?.localities || []).length +
                            (suggest?.projects || []).length +
                            (suggest?.builders || []).length;
                          const idx = baseIdx + i;
                          const active = activeIndex === idx;
                          return (
                            <button
                              key={p.property_id}
                              type="button"
                              role="option"
                              aria-selected={active}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition ${active ? "bg-[#EAF4FC]" : "hover:bg-[#F5F8FB]"}`}
                              onMouseEnter={() => setActiveIndex(idx)}
                              onClick={() => pickSuggestion({ kind: "property", label: p.title, property_id: p.property_id })}
                              data-testid={`sug-prop-${i}`}
                            >
                              <div className="w-10 h-10 rounded-md bg-[#F5F8FB] overflow-hidden shrink-0">
                                {p.images?.[0] && <img src={p.images[0]} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] font-semibold text-[#303030] truncate">{highlight(p.title, city)}</p>
                                <p className="text-[11px] text-[#737373] truncate">{p.locality}, {p.city}</p>
                              </div>
                              <p className="text-[12px] font-bold text-[#0078DB]">{fmtPrice(p.price)}</p>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
            </div>
          )}
        </div>

        {/* Search Button */}
        <button
          type="button"
          onClick={() => onSubmit()}
          className="h-[56px] px-8 text-white text-[15px] font-bold rounded-2xl shrink-0 transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 active:scale-[0.98] shadow-md hover:shadow-blue-500/10"
          style={{ minWidth: 140 }}
          data-testid="search-submit-btn"
        >
          <Search className="w-4 h-4 text-white" />
          <span>Search</span>
        </button>
      </div>

      {/* Secondary Filter Row — tab-aware */}
      <div className="flex items-center gap-2 px-4 lg:px-5 pb-4 lg:pb-5 overflow-x-auto scrollbar-none flex-nowrap lg:flex-wrap">
        {(TAB_FILTERS[activeTab] || []).map((fkey) => (
          <DynamicFilter
            key={`${activeTab}-${fkey}`}
            fkey={fkey}
            isRent={isRent}
            budget={budget}
            setBudget={setBudget}
            budgetTouched={budgetTouched}
            setBudgetTouched={setBudgetTouched}
            budgetMax={budgetMax}
            budgetStep={budgetStep}
            fmtBudget={fmtBudget}
            areaRange={areaRange}
            setAreaRange={setAreaRange}
            areaTouched={areaTouched}
            setAreaTouched={setAreaTouched}
            filters={filters}
            toggleFilter={toggleFilter}
            setFilter={setFilter}
            clearFilter={clearFilter}
          />
        ))}

        {/* Active chip pills (across all multi-select arrays in filters) */}
        <AnimatePresence>
          {Object.entries(filters).flatMap(([k, v]) =>
            Array.isArray(v) ? v.map(val => ({ k, val })) : []
          ).slice(0, 8).map(({ k, val }) => (
            <motion.span
              key={`${k}:${val}`}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.15 }}
              className="inline-flex items-center gap-1 px-2.5 h-7 rounded-full text-[12px] font-semibold border shrink-0"
              style={{ borderColor: BRAND, color: BRAND, background: "#EAF4FC" }}
              data-testid={`active-chip-${val.replace(/[\s/]/g,"-").toLowerCase()}`}
            >
              {val}
              <button
                type="button"
                onClick={() => toggleFilter(k, val)}
                aria-label={`Remove ${val}`}
              >
                <X className="w-3 h-3" />
              </button>
            </motion.span>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ---------- Tab-aware filter renderer ---------- */
function DynamicFilter(props) {
  const {
    fkey, isRent, budget, setBudget, budgetTouched, setBudgetTouched, budgetMax, budgetStep, fmtBudget,
    areaRange, setAreaRange, areaTouched, setAreaTouched,
    filters, toggleFilter, setFilter, clearFilter,
  } = props;

  const Icon = FILTER_ICONS[fkey] || Briefcase;

  // Configs
  if (fkey === "budget" || fkey === "rent") {
    return (
      <RangeSliderFilter
        testid={`filter-${fkey}`}
        label={fkey === "rent" ? "Rent Range" : "Budget"}
        value={budget}
        touched={budgetTouched}
        onChange={(v) => { setBudget(v); setBudgetTouched(true); }}
        onReset={() => { setBudget(isRent ? [10, 100] : [0.5, 5]); setBudgetTouched(false); }}
        min={0}
        max={budgetMax}
        step={budgetStep}
        fmt={fmtBudget}
        title={isRent ? "Monthly Rent" : "Budget Range"}
        icon={Icon}
      />
    );
  }
  if (fkey === "area" || fkey === "plot_area") {
    return (
      <RangeSliderFilter
        testid={`filter-${fkey}`}
        label={fkey === "plot_area" ? "Plot Area" : "Area"}
        value={areaRange}
        touched={areaTouched}
        onChange={(v) => { setAreaRange(v); setAreaTouched(true); }}
        onReset={() => { setAreaRange([0, 5000]); setAreaTouched(false); }}
        min={0}
        max={5000}
        step={50}
        fmt={(n) => n >= 5000 ? "5000+ sqft" : `${n.toLocaleString("en-IN")} sqft`}
        title="Area (sqft)"
        icon={Icon}
      />
    );
  }
  if (fkey === "builder") {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <FilterChipTrigger
            icon={Icon}
            label={filters.builder ? `Builder · ${filters.builder}` : "Builder"}
            testid="filter-builder-trigger"
          />
        </PopoverTrigger>
        <PopoverContent className="w-[260px] p-3 bg-white border border-slate-200/80 rounded-xl shadow-[0_12px_40px_rgba(0,120,219,0.05)]" align="start" side="bottom" avoidCollisions sideOffset={6}>
          <input
            value={filters.builder || ""}
            onChange={(e) => setFilter("builder", e.target.value)}
            placeholder="e.g. Lodha, DLF, Prestige"
            className="w-full border border-[#E5E7EB] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-[#0078DB]"
            data-testid="filter-builder-input"
          />
          <div className="flex items-center justify-between border-t border-[#E5E7EB] mt-3 pt-2">
            <button type="button" onClick={() => clearFilter("builder")} className="text-[12px] font-semibold text-[#737373] hover:text-[#303030]" data-testid="filter-builder-clear">Clear</button>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // Multi-select chip filters (all the remaining keys)
  const CFG = {
    bhk:             { label: "BHK",               options: BHK_OPTIONS },
    construction:    { label: "Construction Status", options: CONSTRUCTION_OPTIONS },
    posted_by:       { label: "Posted By",         options: POSTED_BY_OPTIONS },
    furnishing:      { label: "Furnishing",        options: FURNISHING_OPTIONS },
    availability:    { label: "Availability",      options: AVAILABILITY_OPTIONS },
    ownership:       { label: "Ownership Type",    options: OWNERSHIP_OPTIONS },
    land_type:       { label: "Land Type",         options: LAND_TYPE_OPTIONS },
    possession:      { label: "Possession",        options: POSSESSION_OPTIONS },
    launch_status:   { label: "Launch Status",     options: LAUNCH_STATUS_OPTIONS },
    commercial_type: { label: "Property Type",     options: ["Office Space", "Shop / Showroom", "Warehouse / Godown", "Industrial Building", "Co-working"] },
    project_type:    { label: "Property Type",     options: ["Apartment", "Villa", "Plot", "Builder Floor"] },
  };
  const cfg = CFG[fkey];
  if (!cfg) return null;
  const selected = filters[fkey] || [];
  return (
    <ChipsPopover
      label={selected.length ? `${cfg.label} · ${selected.length}` : cfg.label}
      options={cfg.options}
      selected={selected}
      onToggle={(v) => toggleFilter(fkey, v)}
      onClear={() => clearFilter(fkey)}
      testid={`filter-${fkey}`}
      icon={Icon}
    />
  );
}

function RangeSliderFilter({ testid, label, value, touched, onChange, onReset, min, max, step, fmt, title, icon }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterChipTrigger
          icon={icon}
          label={!touched ? label : `${fmt(value[0])} - ${fmt(value[1])}`}
          testid={`${testid}-trigger`}
        />
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-4 bg-white border border-slate-200/80 rounded-xl shadow-[0_12px_40px_rgba(0,120,219,0.05)]" align="start" side="bottom" avoidCollisions sideOffset={6}>
        <p className="text-[12px] font-bold text-[#303030] mb-3">{title}</p>
        <Slider
          value={value}
          onValueChange={onChange}
          min={min}
          max={max}
          step={step}
          data-testid={`${testid}-slider`}
          className="my-4"
        />
        <div className="flex items-center justify-between text-[12px] text-[#525252] font-semibold">
          <span data-testid={`${testid}-min`}>Min: {fmt(value[0])}</span>
          <span data-testid={`${testid}-max`}>Max: {fmt(value[1])}</span>
        </div>
        <div className="flex items-center justify-between border-t border-[#E5E7EB] mt-4 pt-3">
          <button type="button" onClick={onReset} className="text-[12px] font-semibold text-[#737373] hover:text-[#303030]" data-testid={`${testid}-clear`}>Reset</button>
          <span className="text-[11px] text-[#737373]">Drag to adjust</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ---------- Subcomponents ---------- */
function CheckRow({ label, checked, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded-md hover:bg-[#F5F8FB] transition"
      data-testid={testid}
    >
      <span className="text-[13px] text-[#303030]">{label}</span>
      <span
        className={`w-4 h-4 rounded border grid place-items-center transition ${
          checked ? "border-transparent" : "border-[#D1D5DB]"
        }`}
        style={checked ? { background: "#0078DB" } : {}}
      >
        {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
      </span>
    </button>
  );
}

const FilterChipTrigger = React.forwardRef(({ label, icon: Icon, testid, ...props }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      className="inline-flex items-center gap-2 px-4 h-10 rounded-full border border-slate-200 bg-white text-[13px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-slate-350 transition duration-150 shrink-0 shadow-sm active:scale-95"
      data-testid={testid}
      aria-haspopup="true"
      {...props}
    >
      {Icon && <Icon className="w-4 h-4 text-blue-600 shrink-0" />}
      <span>{label}</span>
      <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 ml-0.5" />
    </button>
  );
});
FilterChipTrigger.displayName = "FilterChipTrigger";

function ChipsPopover({ label, options, selected, onToggle, onClear, testid, icon }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <FilterChipTrigger label={label} icon={icon} testid={`${testid}-trigger`} />
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-3 bg-white border border-slate-200/80 rounded-xl shadow-[0_12px_40px_rgba(0,120,219,0.05)]" align="start" side="bottom" avoidCollisions sideOffset={6}>
        <div className="flex flex-wrap gap-2 mb-3">
          {options.map(o => {
            const on = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                onClick={() => onToggle(o)}
                className={`px-3 h-8 rounded-full text-[12px] font-semibold border transition ${
                  on
                    ? "text-white border-transparent"
                    : "text-[#303030] border-[#E5E7EB] hover:border-[#0078DB] hover:text-[#0078DB] bg-white"
                }`}
                style={on ? { background: "#0078DB" } : {}}
                data-testid={`${testid}-opt-${o.replace(/[\s/]/g,"-").toLowerCase()}`}
                aria-pressed={on}
              >
                {o}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between border-t border-[#E5E7EB] pt-2">
          <button
            type="button"
            onClick={onClear}
            className="text-[12px] font-semibold text-[#737373] hover:text-[#303030]"
            data-testid={`${testid}-clear`}
          >Clear</button>
          <span className="text-[11px] text-[#737373]">{selected.length} selected</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SuggestionGroup({ title, icon: Icon, items, getLabel, getSub, q, onPick, testPrefix, flat, activeIndex, baseIndex }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border-t border-[#F1F3F5] first:border-t-0 py-1">
      <p className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-[#737373]">{title}</p>
      {items.map((it, i) => {
        const idx = baseIndex + i;
        const active = activeIndex === idx;
        return (
          <button
            key={`${getLabel(it)}-${i}`}
            type="button"
            role="option"
            aria-selected={active}
            className={`w-full flex items-center gap-3 px-3 py-2 text-left transition ${active ? "bg-[#EAF4FC]" : "hover:bg-[#F5F8FB]"}`}
            onMouseEnter={() => {}}
            onClick={() => onPick(it)}
            data-testid={`${testPrefix}-${i}`}
          >
            <Icon className="w-4 h-4 text-[#737373] shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-[13px] text-[#303030] truncate">{highlight(getLabel(it), q)}</p>
              {getSub && <p className="text-[11px] text-[#737373] truncate">{getSub(it)}</p>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function fmtPrice(n) {
  if (n == null) return "—";
  if (n >= 1_00_00_000) return `₹${(n / 1_00_00_000).toFixed(2)} Cr`;
  if (n >= 1_00_000) return `₹${(n / 1_00_000).toFixed(2)} L`;
  return `₹${Number(n).toLocaleString("en-IN")}`;
}
